import { pipe } from 'fp-ts/function';
import { DateService, DateTimeUtcService, DateTimeUtils, TimeService } from '@mews-framework/datetime';
import { DateTimeLocalizationInfo } from '@mews-framework/globalization';
import { TimeService as OptimusTimeService, TimePickerProps } from '@optimus-web/core';
import { FieldAdapter } from '../hooks';
import { formatDateValue } from './formatDate';

export const createDateFormatPatternAdapter = (localizationInfo: DateTimeLocalizationInfo, dateFormatPattern: string) =>
    ({
        input: (value) => value || null,
        output: (value) =>
            typeof value === 'string' ? formatDateValue(value, dateFormatPattern, localizationInfo) : (value as string),
    }) satisfies FieldAdapter;

export const createUtcAdapter = (localizationInfo: DateTimeLocalizationInfo, timezone: string) =>
    ({
        input: (value) =>
            typeof value === 'string' && DateService.isValidString(value, localizationInfo)
                ? DateTimeUtils.dateStringToDateTimeUtcString(value, localizationInfo, timezone)
                : value || null,
        output: (value) =>
            typeof value === 'string' && DateTimeUtcService.isValidString(value)
                ? DateTimeUtils.dateTimeUtcStringToDateString(value, localizationInfo, timezone)
                : (value as string),
    }) satisfies FieldAdapter;

export const createIsoAdapter = (localizationInfo: DateTimeLocalizationInfo, timezone: string) =>
    ({
        input: (value) =>
            typeof value === 'string' && DateService.isValidString(value, localizationInfo)
                ? DateTimeUtils.dateStringToDateIsoString(value, localizationInfo, timezone)
                : value || null,
        output: (value) =>
            typeof value === 'string' && DateTimeUtils.isValidDateIsoString(value)
                ? DateTimeUtils.dateIsoStringToDateString(value, localizationInfo)
                : (value as string),
    }) satisfies FieldAdapter;

export interface CreateIsoTimeAdapterParams extends Pick<TimePickerProps, 'showSeconds'> {
    localizationInfo: DateTimeLocalizationInfo;
    timeService: OptimusTimeService;
}
export const createIsoTimeAdapter = ({ localizationInfo, showSeconds, timeService }: CreateIsoTimeAdapterParams) =>
    ({
        input: (value) => {
            if (typeof value === 'string' && value) {
                if (timeService.isValidShortString(value)) {
                    return pipe(value, timeService.fromShortString, timeService.getIsoString);
                } else if (timeService.isValidString(value)) {
                    return pipe(value, timeService.fromString, timeService.getIsoString);
                }
            }
            return value || null;
        },
        output: (value) => {
            if (typeof value === 'string' && value && TimeService.isValidTimeString(value)) {
                const time = TimeService.parse(value);
                if (showSeconds) {
                    return TimeService.toString(time, localizationInfo);
                }
                return TimeService.toShortString(time, localizationInfo);
            }
            return value as string;
        },
    }) satisfies FieldAdapter;

export const nullAdapter = {
    input: (value) => value || null,
} satisfies FieldAdapter;
